// Diagnóstico de saúde de uma base de teste, a partir da última run.
// Apenas leitura/derivação — não altera regra de negócio nem dados persistidos.
// precision/coverage chegam como float 0-1 (ou null se nunca rodou).

export type DiagnosisLevel =
  | 'HEALTHY'
  | 'ATTENTION'
  | 'CRITICAL'
  | 'HIGH_COV_LOW_PREC'
  | 'LOW_COV_HIGH_PREC'
  | 'NO_DATA'

export type DiagnosisTone = 'success' | 'warning' | 'danger' | 'neutral'

export interface Diagnosis {
  level: DiagnosisLevel
  label: string
  tone: DiagnosisTone
  description: string
}

const DIAGNOSIS: Record<DiagnosisLevel, Omit<Diagnosis, 'level'>> = {
  HEALTHY: {
    label: 'Saudável',
    tone: 'success',
    description: 'Precisão e cobertura altas (≥80%). Forte candidata a usar em produção.',
  },
  ATTENTION: {
    label: 'Atenção',
    tone: 'warning',
    description: 'Métricas medianas. Vale revisar regras e iterar antes de promover.',
  },
  CRITICAL: {
    label: 'Crítica',
    tone: 'danger',
    description: 'As regras classificam, mas erram tudo (precisão 0%). Revisar com urgência.',
  },
  HIGH_COV_LOW_PREC: {
    label: 'Alta cobertura · baixa precisão',
    tone: 'danger',
    description: 'Classifica grande parte da base, mas a maioria na classe errada. Refine ou adicione exclusões.',
  },
  LOW_COV_HIGH_PREC: {
    label: 'Baixa cobertura · alta precisão',
    tone: 'warning',
    description: 'Acerta quando classifica, mas pega pouca coisa. Falta regra pra ampliar a cobertura.',
  },
  NO_DATA: {
    label: 'Sem dados',
    tone: 'neutral',
    description: 'Nenhuma run concluída ainda. Rode a base pra ver o diagnóstico.',
  },
}

export function diagnoseBase(input: {
  runsCount: number
  precision: number | null
  coverage: number | null
}): Diagnosis {
  const { runsCount, precision, coverage } = input
  const level = ((): DiagnosisLevel => {
    if (runsCount === 0 || precision == null || coverage == null) return 'NO_DATA'
    if (precision === 0 && coverage > 0) return 'CRITICAL'
    if (precision >= 0.8 && coverage >= 0.8) return 'HEALTHY'
    if (precision < 0.5 && coverage >= 0.7) return 'HIGH_COV_LOW_PREC'
    if (precision >= 0.7 && coverage < 0.5) return 'LOW_COV_HIGH_PREC'
    return 'ATTENTION'
  })()
  return { level, ...DIAGNOSIS[level] }
}

// Tons que representam bases que precisam de ação urgente — usado no resumo.
export const CRITICAL_LEVELS: DiagnosisLevel[] = ['CRITICAL', 'HIGH_COV_LOW_PREC']

// Formatação APENAS visual do nome da base (não altera o valor no banco):
// trim, colapsa espaços, normaliza separadores e aplica sentence case leve.
export function formatBaseName(raw: string): string {
  const cleaned = raw
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*[-–—]\s*/g, ' — ')
  if (cleaned.length === 0) return raw
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}
